import { useEffect, useRef } from "react";
import { useAppDispatch } from "@/store/hooks";
import { User } from "oidc-client-ts";
import { loginFailed, loginSuccess, logout } from "@/store/authSlice";
import { clearUser, setUserInfo } from "@/store/userSlice";
import { AuthService } from "@/service/AuthService";
import { TokenService } from "@/service/TokenService";
import Logger from "@/utils/Logger";
import { AppSettingsService } from "@/service/AppSettingsService";

interface UseAuthStateProps {
  oidcUser: User | null;
  isLoading: boolean;
  error: unknown;
  signinRedirect: () => Promise<void>; 
}

export const useAuthState = ({ 
  oidcUser, 
  isLoading, 
  error,
  signinRedirect 
}: UseAuthStateProps) => {
  const dispatch = useAppDispatch();
  const hasRestoredRedirect = useRef(false);
  const hasHandledError = useRef(false);

  // Synchronize OIDC user state with Redux store
  useEffect(() => {
    try {
      if (oidcUser && !oidcUser.expired) {
        const userProfile = AuthService.extractUserProfile(oidcUser);
        const userInfo = AuthService.extractUserInfo(oidcUser);

        // Set auth state (without roles)
        dispatch(
          loginSuccess({
            token: oidcUser.access_token,
            user: userProfile,
            expiresAt: TokenService.getTokenExpirationTime(oidcUser),
          }),
        );

        // Set user info (with roles)
        dispatch(setUserInfo(userInfo));
        Logger.info("User authenticated - Redux state updated", [
          "useAuthState",
          JSON.stringify({
            user: userProfile,
            roles: userInfo.appRoles,
            userInfo: userInfo,
          }),
        ]);
        
        // Reset error flag on successful auth
        hasHandledError.current = false;
      } else if (
          !AppSettingsService.isOAuthLocalHostBypassEnabled() &&
          window.location.hostname === "localhost"
      ) {
        // Development mode fallback
        const devUser = AuthService.getDevelopmentUser();
        const devUserInfo = AuthService.getDevelopmentUserInfo();

        dispatch(
            loginSuccess({
              token: TokenService.getDevelopmentToken() || "",
              user: devUser,
              expiresAt: Math.floor(Date.now() / 1000) + 3600,
            }),
        );

        dispatch(setUserInfo(devUserInfo));

        Logger.info("Development mode - mock user authenticated", [
          "useAuthState",
          JSON.stringify({
            user: devUser,
            roles: devUserInfo.appRoles,
            userInfo: devUserInfo,
          }),
        ]);
      } else if (!isLoading && !error) {
        AuthService.savePostLoginRedirect();
        dispatch(logout());
        dispatch(clearUser());
        // Logger.info("User not authenticated - URL is saved, Redux state cleared", ["useAuthState"]);
      }
    } catch (e) {
      Logger.error("Error during auth state synchronization", e, ["useAuthState"]);
    }
  }, [oidcUser, isLoading, error, dispatch]);

  // Handle OIDC errors
  // It handles silent renew failures and token expiration immediately
  useEffect(() => {
    if (!error || hasHandledError.current) return;

    hasHandledError.current = true;
    const errorMsg = getErrorMessage(error);
    Logger.error("OIDC authentication error occurred", error, ["useAuthState"]);

    try {
      AuthService.savePostLoginRedirect();
      dispatch(loginFailed(errorMsg));
      dispatch(clearUser());
      dispatch(logout());
    } catch (e) {
      Logger.warn("Failed to update Redux state during auth error", ["useAuthState"]);
    }

    // Handle token exchange failures (401 from /token endpoint)
    if (isTokenExchangeFailure(error)) {
      Logger.warn("Token exchange failed - clearing cached user and forcing re-auth", [
        "useAuthState",
      ]);
      
      try {
        sessionStorage.clear();
      } catch (e) {
        Logger.error("Failed to clear cached OIDC state", e, ["useAuthState"]);
      }

      // Force re-authentication
      AuthService.forceLoginRedirect(signinRedirect);
      return;
    }  
    
    // Immediately trigger re-authentication on silent renew failure
    if (isSilentRenewFailure(error)) {
      Logger.info("Silent renew failed - forcing immediate re-authentication", [
        "useAuthState",
      ]);
      
      // Force redirect to login immediately (don't wait for 401)
      AuthService.forceLoginRedirect(signinRedirect);
    }
  }, [error, dispatch, signinRedirect]);
};

const getErrorMessage = (err: unknown): string => {
  try {
    return String((err as any)?.message || err) || "Unknown error";
  } catch {
    return "Error stringify failed";
  }
};

const isSilentRenewFailure = (err: unknown): boolean => {
  const msg = String((err as any)?.message || "").toLowerCase();
  return (err as any)?.source === "renewSilent" || msg.includes("refresh token");
};

//Detect token exchange failures (401 from /token)
const isTokenExchangeFailure = (err: unknown): boolean => {
  const msg = String((err as any)?.message || "").toLowerCase();
  return (
      msg.includes("invalid credentials") ||
      msg.includes("access is denied") ||
      msg.includes("invalid_grant") ||
      msg.includes("authorization code") ||
      (err as any)?.error === "invalid_grant"
  );
};